import { FC, useEffect, useState } from "react";
import { Drawer } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useLanguage } from "../LanguageContext";
import { useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Snackbar from "@mui/material/Snackbar";
import { userDrawerTranslations } from "./userDrawerTranslations";
import { Language } from "../../Types/types";
import SnackbarContent from "@mui/material/SnackbarContent";
import { GoPersonFill } from "react-icons/go";
import ThemeToggle from "./themeToggle";
import { jwtDecode } from "jwt-decode";

interface UserDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface TokenPayload {
  iat: number;
  exp: number;
  roles: string[];
  username: string;
  clientIdentifier: string;
}

const UserDrawer: FC<UserDrawerProps> = ({ open, onClose }) => {
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [username, setUsername] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const t = userDrawerTranslations[language as Language];

  useEffect(() => {
    if (open) {
      const storedUsername = localStorage.getItem("username");
      const token = localStorage.getItem("token");

      if (storedUsername) {
        setUsername(storedUsername);
      }

      if (token) {
        try {
          const decodedToken = jwtDecode<TokenPayload>(token);
          setIsAdmin(decodedToken.roles.includes("ROLE_ADMIN"));
        } catch (error) {
          console.error("Invalid token", error);
        }
      }
    }
  }, [open]);

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    onClose();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
    onClose();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(username);
    setSnackbarOpen(true);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isSmallScreen ? "100%" : 380,
          minWidth: 320,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(88, 110, 223, .8)",
        },
      }}
    >
      <div className="bg-white dark:bg-darkSecondaryBackground h-screen flex flex-col">
        <div className="p-4 flex flex-col h-full">
          <div className="flex justify-between items-center p-2">
            <ThemeToggle />
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="rounded-full p-2 hover:bg-black/5 dark:hover:bg-white/10 text-black dark:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
            >
              <MenuIcon />
            </button>
          </div>

          <div className="flex flex-col h-full">
            {/* Username card */}
            <div className="flex items-center gap-3 bg-lightPurple dark:bg-darkBackground/60 border border-purple/40 dark:border-purple/50 rounded-lg px-3 py-2 shadow-sm">
              <div className="text-black dark:text-white">
                <GoPersonFill style={{ fontSize: "1.25rem" }} />
              </div>
              <span className="text-sm font-medium text-black dark:text-white truncate">
                {username || t.usernamePlaceholder}
              </span>
              <button
                onClick={handleCopy}
                aria-label="Copy username"
                className="ml-auto inline-flex items-center justify-center rounded-md p-2 hover:bg-black/5 dark:hover:bg-white/10 text-black dark:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
              >
                <ContentCopyIcon fontSize="small" className="text-black dark:text-white" />
              </button>
            </div>

            {/* Language Selection */}
            <div className="mt-6">
              <p className="px-2 text-xs uppercase tracking-wider text-gray-500 dark:text-white mb-2">
                Language
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  "cz",
                  "en",
                  "fr",
                  "es",
                  "nl",
                  "sk",
                  "de",
                  "hu",
                  "pl",
                  "fi",
                  "no",
                  "da",
                  "ru",
                  "sv",
                  "ar",
                ].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className="p-3 rounded-lg bg-white shadow-sm dark:bg-darkBackground/60 border border-transparent dark:border-purple/30 hover:shadow-md dark:hover:bg-darkBackground/70 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
                  >
                    <img
                      src={`/flags/${lang}.png`}
                      alt={lang}
                      className="h-6 w-auto"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex flex-col justify-end mt-auto gap-2">
              {isAdmin && (
                <button
                  onClick={() => {
                    navigate("/licenses-list");
                    onClose();
                  }}
                  className="bg-lightPurple text-black dark:text-white dark:bg-darkBackground/60 ring-1 ring-purple/40 dark:ring-purple/40 hover:bg-lightPurple/90 dark:hover:bg-darkBackground/70 font-semibold w-full rounded-lg px-4 py-2"
                >
                  {t.adminZoneButton}
                </button>
              )}
              <button
                onClick={handleLogout}
                className="bg-purple hover:bg-darkPurple text-white w-full px-4 py-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/50"
              >
                {t.logoutButton}
              </button>
            </div>
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <SnackbarContent
            message={t.usernameCopied}
            sx={{
              backgroundColor: "orange",
              color: "white",
            }}
          />
        </Snackbar>
      </div>
    </Drawer>
  );
};

export default UserDrawer;
